/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RepositoryMain.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Typography } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import SyncIcon from "@mui/icons-material/Sync";
import AddTestCaseDialog from "./components/AddTestCaseDialog";
import UpdateTestCaseDialog from "./components/UpdateTestCaseDialog";
import { useDispatch, useSelector } from "react-redux";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import { HandleApiActions } from "../../redux/actions";
import testCasesActions from "../../redux/actions/testCasesActions";
import SyncTestCaseDialog from "./components/SyncTestCaseDialog";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import BorderColorIcon from "@mui/icons-material/BorderColor";

const formatStatus = (status) => {
  if (status === undefined || status === null || status === "") return "-";
  return status
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

function RepositoryMain() {
  const dispatch = useDispatch();

  const profileDetails = useSelector((store) => store.profileReducer);

  const [openAddTestCaseDialog, setOpenAddTestCaseDialog] = useState(false);
  const [openUpdateTestCaseDialog, setOpenUpdateTestCaseDialog] =
    useState(false);
  const [openSyncDialog, setOpenSyncDialog] = useState(false);

  const [tableRowsData, setTableRowsData] = useState([]);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [selectedTestCaseData, setSelectedTestCaseData] = useState(null);

  const searchQueryRef = useRef("");
  const [totalCount, setTotalCount] = useState(0);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [getRawTestCases, setGetRawTestCases] = useState([]);

  const fetchTestCases = useCallback(
    async (options = {}) => {
      const {
        force = false,
        pageOverride = null,
        rowsPerPageOverride = null,
      } = options;
      const pageToUse = pageOverride ?? page;
      const rowsPerPageToUse = rowsPerPageOverride ?? rowsPerPage;
      const response = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_test_cases,
          params: {
            page_number: pageToUse,
            page_size: rowsPerPageToUse,
            sort_column: "created_at",
            sort_order: "DESC",
          },
          show_toast: false,
        }),
      );

      if (response && !response.error) {
        console.log("API Response:", response);
        if (!isSearchActive || force) {
          if (force) {
            setIsSearchActive(false);
          }
          setTotalCount(response.total_count || 0);
          setGetRawTestCases(
            Array.isArray(response.data) ? response.data : [],
          );
        } else {
          console.log("Skipped updating master list because search is active");
        }
      }
    },
    [dispatch, isSearchActive, page, rowsPerPage],
  );

  useEffect(() => {
    fetchTestCases();
  }, [fetchTestCases]);

  useEffect(() => {
    if (!isSearchActive) {
      fetchTestCases();
    }
  }, [page, rowsPerPage, isSearchActive, fetchTestCases]);

  useEffect(() => {
    const formattedData = (Array.isArray(getRawTestCases)
      ? getRawTestCases
      : []
    ).map((tc, index) => ({
        ...tc,
        id: tc?.id || tc?.test_case_id || index + 1,
        sno: (page - 1) * rowsPerPage + index + 1,
        description: tc?.description,
        status: tc?.status,
        user_details: {
          name: tc?.created_by_name,
        },
        created_at: convertUtcToTimeZone(
          tc?.created_at,
          profileDetails?.profile?.profile?.region,
        ),
      }));

    setTableRowsData(formattedData);
  }, [
    getRawTestCases,
    page,
    rowsPerPage,
    profileDetails?.profile?.profile?.region,
  ]);

  console.log("Table Rows Data:", tableRowsData);

  const handleUpdateTestCase = (params) => {
    setSelectedTestCaseData({ ...params?.row });
    setOpenUpdateTestCaseDialog(true);
  };

  const breadcrumbItems = [{ label: "Test Cases" }, { label: "Repository" }];

  const COLUMNS = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
        filterable: false,
        sortable: false,
      },
      {
        field: "manual_test_case_id",
        headerName: "Test Case ID",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "name",
        headerName: "Test Case Name",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "need_new_vehicle",
        headerName: "Need New Vehicle",
        flex: 1,
        minWidth: 140,
        renderCell: (params) =>
          params.row.need_new_vehicle === true ? "Yes" : "No",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: 11,
            }}
          >
            {formatStatus(params.row.status)}
          </Typography>
        ),
      },
      {
        field: "user_details",
        headerName: "User",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Box>{params?.row?.user_details?.name || params?.row?.created_by}</Box>
        ),
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "update",
        headerName: "Update",
        minWidth: 120,
        renderCell: (params) => (
          <IconButton
            size="small"
            sx={{ color: "#000000", p: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              handleUpdateTestCase(params);
            }}
          >
            <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
          </IconButton>
        ),
      },
      {
        field: "source",
        headerName: "Source",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: 10,
              textTransform: "uppercase",
            }}
          >
            {params.row.source || "-"}
          </Typography>
        ),
      },
    ],
    [],
  );
  const handleSearchData = (data, resp) => {
    const currentQuery = (searchQueryRef.current || "").trim();
    if (data === null || currentQuery === "") {
      setPage(1);
      setIsSearchActive(false);
      setTotalCount(resp?.total_count ?? 0);
      setGetRawTestCases(Array.isArray(resp?.data) ? resp.data : []);
    } else if (Array.isArray(resp?.data) && resp.data.length > 0) {
      setPage(1);
      setIsSearchActive(true);
      setTotalCount(resp?.total_count ?? 0);
      setGetRawTestCases(resp.data);
    } else {
      setPage(1);
      setIsSearchActive(true);
      setGetRawTestCases([]);
      setTotalCount(0);
    }
  };

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
          flexShrink: 0,
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            gap: 1,
          }}
        >
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: 1,
                page_size: rowsPerPage,
              }}
              apiAction={testCasesActions.get_test_cases}
              searchKey={"name"}
              getDataOnSearch={handleSearchData}
              onSearchQueryChange={(value = "") => {
                searchQueryRef.current = value;
              }}
              placeholder="Search Test Case Name"
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            gap: 1,
          }}
        >
          <Box>
            <CustomButtonV2
              type="submit"
              size="small"
              variant="contained"
              btnName="Sync"
              handleOnClick={() => setOpenSyncDialog(true)}
              startIcon={<SyncIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={() => setOpenAddTestCaseDialog(true)} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          height: "78vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: { xs: "0 8px", md: "0 16px" },
          mt: -1,
        }}
      >
        <CustomDataGridV2
          rows={tableRowsData}
          columns={COLUMNS}
          isMultiLine={true}
          onRowClick={handleUpdateTestCase}
          showPagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => {
            setPage(newPage);
            if (!isSearchActive) {
              fetchTestCases({ pageOverride: newPage });
            }
          }}
          handlePageSizeChange={(newSize) => {
            setRowsPerPage(newSize);
            setPage(1);
            if (!isSearchActive) {
              fetchTestCases({
                pageOverride: 1,
                rowsPerPageOverride: newSize,
              });
            }
          }}
          totalItems={totalCount}
        />
      </Box>
      <AddTestCaseDialog
        open={openAddTestCaseDialog}
        onClose={setOpenAddTestCaseDialog}
        onSuccess={() => {
          setPage(1);
          fetchTestCases({ force: true, pageOverride: 1 });
        }}
      />
      <UpdateTestCaseDialog
        open={openUpdateTestCaseDialog}
        onClose={setOpenUpdateTestCaseDialog}
        selectedTestCaseData={selectedTestCaseData}
        setSelectedTestCaseData={setSelectedTestCaseData}
        onSuccess={() => {
          setPage(1);
          fetchTestCases({ force: true, pageOverride: 1 });
        }}
      />
      <SyncTestCaseDialog open={openSyncDialog} onClose={setOpenSyncDialog} />
    </Box>
  );
}

export default RepositoryMain;
